/**
 * API Configuration & Axios Instance
 * Centralized API setup for the Taska Ummu Qaseh PWA
 */
import axios from "axios";
import secureStorage from "../utils/secureStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Simple in-memory cache for API responses
const apiCache = new Map();
const CACHE_DURATION = 30 * 1000; // 30 seconds cache

export const getCachedData = (key) => {
  const cached = apiCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

export const setCachedData = (key, data) => {
  apiCache.set(key, { data, timestamp: Date.now() });
};

export const clearCache = (keyPattern) => {
  if (keyPattern) {
    for (const key of apiCache.keys()) {
      if (key.includes(keyPattern)) {
        apiCache.delete(key);
      }
    }
  } else {
    apiCache.clear();
  }
};

export const axiosInstance = axios.create({
  baseURL: API,
});

// Flag to prevent multiple concurrent logouts
let isLoggingOut = false;

export const getIsLoggingOut = () => isLoggingOut;
export const setIsLoggingOut = (val) => { isLoggingOut = val; };

axiosInstance.interceptors.request.use((config) => {
  const token = secureStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to cache GET requests (skip blob/arraybuffer responses)
axiosInstance.interceptors.response.use(
  (response) => {
    if (response.config.method === 'get' && !response.config.responseType) {
      const cacheKey = response.config.url;
      setCachedData(cacheKey, response.data);
    }
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthEndpoint = url.includes('/auth/login') || 
                             url.includes('/auth/register') || 
                             url.includes('/auth/parent') ||
                             url.includes('/auth/token-status') ||
                             url.includes('/auth/refresh-token') ||
                             url.includes('/auth/me') ||
                             url.includes('/auth/forgot-password') ||
                             url.includes('/auth/reset-password') ||
                             url.includes('/auth/verify-email');
      
      if (!isAuthEndpoint && !isLoggingOut) {
        isLoggingOut = true;
        console.warn('[AUTH] Token expired or invalid, clearing session');
        secureStorage.removeItem('token');
        secureStorage.removeItem('user');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login?expired=true';
        }
        setTimeout(() => { isLoggingOut = false; }, 3000);
      }
    }

    // Handle 403 BRANCH_DISABLED - cawangan dinonaktifkan sementara oleh superadmin
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail || '';
      if (typeof detail === 'string' && detail.startsWith('BRANCH_DISABLED') && !isLoggingOut) {
        isLoggingOut = true;
        console.warn('[AUTH] Branch disabled by superadmin, clearing session');
        secureStorage.removeItem('token');
        secureStorage.removeItem('user');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login?branch_disabled=true';
        }
        setTimeout(() => { isLoggingOut = false; }, 3000);
      }
    }
    // Normalkan respons ralat bukan-JSON (cth. halaman HTML Cloudflare 52x / 5xx proxy)
    // supaya semua `error.response?.data?.detail` di seluruh app memaparkan mesej mesra
    if (error.response) {
      const { data, status } = error.response;
      const isNonJson = typeof data !== 'object' || data === null;
      if (isNonJson || (status >= 500 && data?.detail === undefined)) {
        error.response.data = { detail: getApiErrorMessage(error), raw_status: status, non_json: isNonJson };
      }
    }
    return Promise.reject(error);
  }
);

// Tukar ralat axios kepada mesej mesra (termasuk respons bukan-JSON seperti halaman Cloudflare)
export const getApiErrorMessage = (error, fallback = "Ralat tidak diketahui") => {
  if (!error?.response) {
    return "Tiada sambungan ke pelayan. Sila semak internet dan cuba lagi.";
  }
  const { status, data } = error.response;
  const detail = data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object') {
    if (detail.message) return detail.message;
    if (Array.isArray(detail)) {
      const msgs = detail.map(d => d?.msg || d?.message).filter(Boolean);
      if (msgs.length) return msgs.join(', ');
    }
  }
  const isNonJson = typeof data === 'string' || data === undefined || data === null || data === '';
  if (status >= 520 && status <= 526) {
    return `Pelayan tidak memberi respons yang sah (ralat ${status}). Data mungkin telah disimpan — sila muat semula dan semak sebelum mencuba lagi.`;
  }
  if (status >= 500) {
    return `Pelayan tidak dapat memproses permintaan (ralat ${status}). Sila cuba sebentar lagi.`;
  }
  if (isNonJson) {
    return `Permintaan gagal (ralat ${status}). Sila cuba sebentar lagi.`;
  }
  return fallback;
};

// Loading fallback component
export const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-3"></div>
      <p className="text-purple-600 text-sm font-medium animate-pulse">Memuatkan...</p>
    </div>
  </div>
);
